import { Controller } from '@hotwired/stimulus';
import Cookies from 'js-cookie';
import events from 'lib/events';
import { featureEnabled } from 'lib/helpers';

export default class SearchAnalyticsController extends Controller {
  connect() {
    if (!this.isSearchByImageResultsPage) {
      window.addEventListener(events.search.click, this.sendSearchClick);
      window.addEventListener(events.search.sponsored_click, this.sendSponsoredSearchClick);
      window.addEventListener(events.download_btn.initial_click, this.sendSearchDownload);

      if (this.isSearchResultsPage) {
        this.sendPageView();
      }
    }
  }

  disconnect() {
    window.removeEventListener(events.search.click, this.sendSearchClick);
    window.removeEventListener(events.search.sponsored_click, this.sendSponsoredSearchClick);
    window.removeEventListener(events.download_btn.initial_click, this.sendSearchDownload);
  }

  sendBeacon(url, data) {
    if (document.body.getAttribute('data-env') === 'test') {
      const searchEventsSent = Cookies.getJSON('searchEventsSent') || [];
      searchEventsSent.push({ url, data });
      Cookies.set('searchEventsSent', searchEventsSent, { domain: '.example.com' });
      return;
    }

    const formData = new FormData();
    formData.append(this.csrfParam, this.csrfToken);

    Object.keys(data).forEach((key) => {
      formData.append(key, data[key]);
    });

    window.navigator.sendBeacon(url, formData);
  }

  sendPageView() {
    if (!featureEnabled('search-tracking')) return;

    this.sendBeacon('/api/v2/tracking/search/pageviews', this.pageViewData);
  }

  sendSearchClick = (event) => {
    const clickData = this.searchClickData(event);

    this.sendBeacon('/api/v2/tracking/search/clicks', clickData);

    // save search data in localStorage for tracking possible download event
    window.localStorage.setItem(`resource-${event.detail.resourceId}`, JSON.stringify(clickData));
  }

  sendSponsoredSearchClick = (event) => {
    const sponsoredClickData = this.searchSponsoredClickData(event);

    this.sendBeacon('/api/v2/tracking/search/sponsored_clicks', sponsoredClickData);
  }

  sendSearchDownload = (event) => {
    const downloadData = this.searchDownloadData(event);

    if (downloadData) {
      this.sendBeacon('/api/v2/tracking/search/downloads', downloadData);
    }
  }

  get csrfParam() {
    const csrfParamTag = document.querySelector('meta[name=csrf-param]');
    if (csrfParamTag) {
      return csrfParamTag.content;
    }

    return null;
  }

  get csrfToken() {
    const csrfTokenTag = document.querySelector('meta[name=csrf-token]');
    if (csrfTokenTag) {
      return csrfTokenTag.content;
    }

    return null;
  }

  get isSearchResultsPage() {
    // split-test: iscroll_modal - extra conditional
    // Make sure we're on a search results page,
    // but not in the show page modal view.
    if (document.querySelector('[data-search-results-count]')
       && !document.querySelector('#resource-show-modal-view')) {
      return true;
    }
    return false;
  }

  get isSearchByImageResultsPage() {
    return document.body.classList.contains('searches-show_by_image');
  }

  get pageViewData() {
    const fields = [
      'actual_free_results_shown_count',
      'actual_pro_results_shown_count',
      'algo_version',
      'content_type',
      'domain',
      'filters_used',
      'filter_params_json',
      'guid',
      'ignore',
      'internal',
      'locale',
      'max_free_results_shown_count',
      'max_pro_results_shown_count',
      'page',
      'pageview_guid',
      'pro',
      'referrer',
      'results_count',
      'split_test_name',
      'split_test_variant',
      'term',
      'total_free_results',
      'total_pro_results',
      'type',
      'user_guid',
    ];

    const data = {};

    fields.forEach((field) => {
      data[field] = this.element.getAttribute(`data-search-${field.replace(/_/g, '-')}`) || '';
    });

    return data;
  }

  searchClickData(event) {
    const data = this.pageViewData;
    data.resource_id = event.detail.resourceId;
    data.resource_position = event.detail.resourcePosition;
    data.resource_pro = event.detail.resourcePro;
    return data;
  }

  searchSponsoredClickData(event) {
    const data = this.pageViewData;
    data.resource_id = event.detail.resourceId;
    data.resource_position = event.detail.resourcePosition;
    data.resource_pro = event.detail.resourcePro;
    data.sponsored = event.detail.sponsored;
    return data;
  }

  searchDownloadData(event) {
    if (!event.detail.guid) {
      // If we don't have the search guid, we don't want to track this download
      return null;
    }

    const data = {};

    Object.entries(event.detail).forEach(([key, value]) => {
      data[key] = value;
    });

    return data;
  }
}
